import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { X } from "lucide-react";
import type { CreateOrderData } from "@/hooks/useOrders";
import { usePaymentStatuses } from "@/hooks/usePaymentStatuses";

interface OrderFormProps {
  onSubmit: (orderData: CreateOrderData) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<CreateOrderData>;
}

export function OrderForm({ onSubmit, onCancel, initialData }: OrderFormProps) {
  const { paymentStatuses, loading: paymentStatusesLoading } = usePaymentStatuses();
  const [formData, setFormData] = useState<CreateOrderData>({
    product_id: "",
    product_name: "",
    product_category: "",
    product_price: 0,
    product_original_price: 0,
    customer_first_name: "",
    customer_last_name: "",
    customer_email: "",
    customer_phone: "",
    delivery_address: "",
    delivery_city: "",
    delivery_state: "",
    delivery_pincode: "",
    custom_photo_url: "",
    custom_text: "",
    special_remarks: "",
    payment_method: "Razorpay",
    payment_status: "pending",
    payment_amount: 0,
    order_status: "pending_payment",
    estimated_delivery_date: "",
    expected_production_date: "",
    is_custom_order: false,
    priority_level: "normal",
    notes: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.product_name.trim()) {
      newErrors.product_name = "Product name is required";
    }
    if (!formData.product_category.trim()) {
      newErrors.product_category = "Product category is required";
    }
    if (formData.product_price <= 0) {
      newErrors.product_price = "Product price must be greater than 0";
    }
    if (!formData.customer_first_name.trim()) {
      newErrors.customer_first_name = "First name is required";
    }
    if (!formData.customer_last_name.trim()) {
      newErrors.customer_last_name = "Last name is required";
    }
    if (!formData.customer_email.trim()) {
      newErrors.customer_email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.customer_email)) {
      newErrors.customer_email = "Email is invalid";
    }
    if (!formData.customer_phone.trim()) {
      newErrors.customer_phone = "Phone number is required";
    }
    if (!formData.delivery_address.trim()) {
      newErrors.delivery_address = "Delivery address is required";
    }
    if (!formData.delivery_city.trim()) {
      newErrors.city = "City is required";
    }
    if (!formData.delivery_state.trim()) {
      newErrors.state = "State is required";
    }
    if (!formData.delivery_pincode.trim()) {
      newErrors.pincode = "Pincode is required";
    }
    if (formData.payment_amount <= 0) {
      newErrors.payment_amount = "Payment amount must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error("Error submitting order:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof CreateOrderData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const generateProductId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `PROD-${timestamp}-${random}`;
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Create New Order</CardTitle>
            <CardDescription>
              Add a new customer order to the system
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Product Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Product Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="product_name">Product Name *</Label>
                <Input
                  id="product_name"
                  value={formData.product_name}
                  onChange={(e) => handleInputChange("product_name", e.target.value)}
                  className={errors.product_name ? "border-red-500" : ""}
                />
                {errors.product_name && (
                  <p className="text-sm text-red-500">{errors.product_name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="product_category">Product Category *</Label>
                <Input
                  id="product_category"
                  value={formData.product_category}
                  onChange={(e) => handleInputChange("product_category", e.target.value)}
                  className={errors.product_category ? "border-red-500" : ""}
                />
                {errors.product_category && (
                  <p className="text-sm text-red-500">{errors.product_category}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="product_price">Product Price (INR) *</Label>
                <Input
                  id="product_price"
                  type="number"
                  step="0.01"
                  value={formData.product_price}
                  onChange={(e) => handleInputChange("product_price", parseFloat(e.target.value) || 0)}
                  className={errors.product_price ? "border-red-500" : ""}
                />
                {errors.product_price && (
                  <p className="text-sm text-red-500">{errors.product_price}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="product_original_price">Original Price (INR)</Label>
                <Input
                  id="product_original_price"
                  type="number"
                  step="0.01"
                  value={formData.product_original_price}
                  onChange={(e) => handleInputChange("product_original_price", parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Customer Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer_first_name">First Name *</Label>
                <Input
                  id="customer_first_name"
                  value={formData.customer_first_name}
                  onChange={(e) => handleInputChange("customer_first_name", e.target.value)}
                  className={errors.customer_first_name ? "border-red-500" : ""}
                />
                {errors.customer_first_name && (
                  <p className="text-sm text-red-500">{errors.customer_first_name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_last_name">Last Name *</Label>
                <Input
                  id="customer_last_name"
                  value={formData.customer_last_name}
                  onChange={(e) => handleInputChange("customer_last_name", e.target.value)}
                  className={errors.customer_last_name ? "border-red-500" : ""}
                />
                {errors.customer_last_name && (
                  <p className="text-sm text-red-500">{errors.customer_last_name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_email">Email *</Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => handleInputChange("customer_email", e.target.value)}
                  className={errors.customer_email ? "border-red-500" : ""}
                />
                {errors.customer_email && (
                  <p className="text-sm text-red-500">{errors.customer_email}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_phone">Phone Number *</Label>
                <Input
                  id="customer_phone"
                  value={formData.customer_phone}
                  onChange={(e) => handleInputChange("customer_phone", e.target.value)}
                  className={errors.customer_phone ? "border-red-500" : ""}
                />
                {errors.customer_phone && (
                  <p className="text-sm text-red-500">{errors.customer_phone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Delivery Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Delivery Information</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="delivery_address">Delivery Address *</Label>
                <Textarea
                  id="delivery_address"
                  value={formData.delivery_address}
                  onChange={(e) => handleInputChange("delivery_address", e.target.value)}
                  className={errors.delivery_address ? "border-red-500" : ""}
                  rows={3}
                />
                {errors.delivery_address && (
                  <p className="text-sm text-red-500">{errors.delivery_address}</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="delivery_city">City *</Label>
                  <Input
                    id="delivery_city"
                    value={formData.delivery_city}
                    onChange={(e) => handleInputChange("delivery_city", e.target.value)}
                    className={errors.city ? "border-red-500" : ""}
                  />
                  {errors.city && (
                    <p className="text-sm text-red-500">{errors.city}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delivery_state">State *</Label>
                  <Input
                    id="delivery_state"
                    value={formData.delivery_state}
                    onChange={(e) => handleInputChange("delivery_state", e.target.value)}
                    className={errors.state ? "border-red-500" : ""}
                  />
                  {errors.state && (
                    <p className="text-sm text-red-500">{errors.state}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delivery_pincode">Pincode *</Label>
                  <Input
                    id="delivery_pincode"
                    value={formData.delivery_pincode}
                    onChange={(e) => handleInputChange("delivery_pincode", e.target.value)}
                    className={errors.pincode ? "border-red-500" : ""}
                  />
                  {errors.pincode && (
                    <p className="text-sm text-red-500">{errors.pincode}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Custom Order Options */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Custom Order Options</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="custom_photo_url">Custom Photo URL</Label>
                <Input
                  id="custom_photo_url"
                  type="url"
                  value={formData.custom_photo_url}
                  onChange={(e) => handleInputChange("custom_photo_url", e.target.value)}
                  placeholder="https://example.com/photo.jpg"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="custom_text">Custom Text/Message</Label>
                <Input
                  id="custom_text"
                  value={formData.custom_text}
                  onChange={(e) => handleInputChange("custom_text", e.target.value)}
                  placeholder="Custom text or message"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="special_remarks">Special Remarks</Label>
              <Textarea
                id="special_remarks"
                value={formData.special_remarks}
                onChange={(e) => handleInputChange("special_remarks", e.target.value)}
                placeholder="Any special instructions or remarks"
                rows={2}
              />
            </div>
          </div>

          {/* Payment and Order Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Payment & Order Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="payment_method">Payment Method</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) => handleInputChange("payment_method", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Razorpay">Razorpay</SelectItem>
                    <SelectItem value="Cash on Delivery">Cash on Delivery</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_status">Payment Status</Label>
                <Select
                  value={formData.payment_status}
                  onValueChange={(value) => handleInputChange("payment_status", value)}
                  disabled={paymentStatusesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={paymentStatusesLoading ? "Loading..." : "Select payment status"} />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentStatusesLoading ? (
                      <SelectItem value="" disabled>Loading payment statuses...</SelectItem>
                    ) : (
                      paymentStatuses.map((status) => (
                        <SelectItem key={status.id} value={status.name}>
                          {status.name.charAt(0).toUpperCase() + status.name.slice(1)}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_amount">Payment Amount (INR) *</Label>
                <Input
                  id="payment_amount"
                  type="number"
                  step="0.01"
                  value={formData.payment_amount}
                  onChange={(e) => handleInputChange("payment_amount", parseFloat(e.target.value) || 0)}
                  className={errors.payment_amount ? "border-red-500" : ""}
                />
                {errors.payment_amount && (
                  <p className="text-sm text-red-500">{errors.payment_amount}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="order_status">Order Status</Label>
                <Select
                  value={formData.order_status}
                  onValueChange={(value) => handleInputChange("order_status", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending_payment">Pending Payment</SelectItem>
                    <SelectItem value="payment_confirmed">Payment Confirmed</SelectItem>
                    <SelectItem value="order_assigned">Order Assigned to Artist/Seller</SelectItem>
                    <SelectItem value="design_production">Production in Progress</SelectItem>
                    <SelectItem value="quality_check">Quality Check</SelectItem>
                    <SelectItem value="ready_dispatch">Ready for Dispatch</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled_refunded">Cancelled/Refunded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="estimated_delivery_date">Estimated Delivery Date</Label>
                <Input
                  id="estimated_delivery_date"
                  type="date"
                  value={formData.estimated_delivery_date}
                  onChange={(e) => handleInputChange("estimated_delivery_date", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expected_production_date">Expected Production Date</Label>
                <Input
                  id="expected_production_date"
                  type="date"
                  value={formData.expected_production_date}
                  onChange={(e) => handleInputChange("expected_production_date", e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priority_level">Priority Level</Label>
                <Select
                  value={formData.priority_level}
                  onValueChange={(value) => handleInputChange("priority_level", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Checkbox
                  id="is_custom_order"
                  checked={formData.is_custom_order}
                  onCheckedChange={(checked) => handleInputChange("is_custom_order", checked)}
                />
                <Label htmlFor="is_custom_order">Custom Order</Label>
              </div>
            </div>
          </div>

          {/* Admin Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Admin Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder="Any additional notes for admin reference"
              rows={3}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating Order..." : "Create Order"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
} 